import PropTypes from "prop-types";
import * as React from "react";
import {
  Alert,
  Button,
  Drawer,
  Link,
  Skeleton,
  Typography,
} from "../../themeV2";
import AttentionList from "./AttentionList";
import Section from "./Section";
import SetupChecks from "./SetupChecks";
import css from "./ShopInfo.module.css";
import StatList from "./StatList";
import StatusChips from "./StatusChips";
import {
  orderStats,
  payoutStats,
  peopleStats,
  salesOverviewHref,
  shopStats,
  statusChips,
} from "./utils";

const SectionLink = ({ to, children }) => (
  <Link to={to} color="primary" underline="always">
    <Typography variant="label-lg" as="span">
      {children}
    </Typography>
  </Link>
);

SectionLink.propTypes = {
  to: PropTypes.string.isRequired,
  children: PropTypes.node,
};

const LoadingBody = () => (
  <div className={css.loading}>
    {[1, 2, 3, 4].map((row) => (
      <Skeleton key={row} variant="rect" height={64} />
    ))}
  </div>
);

export default function ShopInfoDrawer({
  open,
  onClose,
  shopInfo,
  isLoading,
  isError,
  onRetry,
}) {
  const handleOpenChange = React.useCallback(
    (next) => {
      if (!next) {
        onClose();
      }
    },
    [onClose],
  );

  const shopRoute = shopInfo?.shop?.route;
  const incompleteHref = salesOverviewHref(shopRoute, "incomplete");
  const notReceivedHref = salesOverviewHref(shopRoute, "notReceived");

  return (
    <Drawer.Root open={open} onOpenChange={handleOpenChange} side="right">
      <Drawer.Popup size="md">
        <Drawer.Header>
          <Drawer.Title>{shopInfo?.shop?.name ?? "Shop info"}</Drawer.Title>
          <Drawer.Close />
        </Drawer.Header>

        <Drawer.Body className={css.drawerBody} aria-busy={isLoading}>
          {isError ? (
            <Alert severity="error" title="Shop info could not be loaded">
              <Button variant="text" size="sm" onClick={onRetry}>
                Try again
              </Button>
            </Alert>
          ) : null}

          {isLoading && !isError ? <LoadingBody /> : null}

          {shopInfo && !isError ? (
            <React.Fragment>
              <div className={css.chips}>
                <StatusChips chips={statusChips(shopInfo)} />
              </div>

              {shopInfo.attention.items.length > 0 ? (
                <Section id="attention" title="Needs attention">
                  <AttentionList items={shopInfo.attention.items} />
                </Section>
              ) : null}

              {shopInfo.orders ? (
                <Section id="orders" title="Orders">
                  <StatList rows={orderStats(shopInfo)} />
                  <div className={css.linkRow}>
                    {incompleteHref ? (
                      <SectionLink to={incompleteHref}>
                        View incomplete orders
                      </SectionLink>
                    ) : null}
                    {notReceivedHref ? (
                      <SectionLink to={notReceivedHref}>
                        View awaiting arrival
                      </SectionLink>
                    ) : null}
                  </div>
                </Section>
              ) : null}

              <Section id="setup" title="Setup">
                <SetupChecks checks={shopInfo.setup.checks} />
                {shopRoute ? (
                  <div className={css.linkRow}>
                    <SectionLink to={`/Dashboard/s/${shopRoute}?settings=1`}>
                      Open shop settings
                    </SectionLink>
                  </div>
                ) : null}
              </Section>

              <Section id="payout" title="Payout">
                <StatList rows={payoutStats(shopInfo)} />
              </Section>

              <Section id="people" title="People">
                <StatList rows={peopleStats(shopInfo)} />
              </Section>

              <Section id="shop" title="Shop">
                <StatList rows={shopStats(shopInfo)} />
                {shopRoute ? (
                  <div className={css.linkRow}>
                    <Link
                      href={`/${shopRoute}`}
                      external
                      color="primary"
                      underline="always"
                    >
                      <Typography variant="label-lg" as="span">
                        View live shop
                      </Typography>
                    </Link>
                  </div>
                ) : null}
              </Section>
            </React.Fragment>
          ) : null}
        </Drawer.Body>
      </Drawer.Popup>
    </Drawer.Root>
  );
}

ShopInfoDrawer.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  shopInfo: PropTypes.object,
  isLoading: PropTypes.bool,
  isError: PropTypes.bool,
  onRetry: PropTypes.func,
};
